import React, { createContext, useContext, useState, useEffect } from 'react';
import { requestNotificationPermission, onMessageListener } from '../services/firebase.ts';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext.tsx';

interface NotificationContextType {
  isPermissionGranted: boolean;
  requestPermission: () => Promise<boolean>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  // The browser Notification API does not reflect native iOS/Android permission.
  const getInitialPermission = (): boolean => {
    if (Capacitor.isNativePlatform()) return false;
    if (typeof Notification === 'undefined') {
      return false;
    }
    return Notification.permission === 'granted';
  };

  const [isPermissionGranted, setIsPermissionGranted] = useState<boolean>(getInitialPermission());
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Register the native FCM token after authentication so saveToken has the
  // bearer token required by /user/UpdateUserToken. This also refreshes the
  // backend record whenever the app starts with an existing login.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !Capacitor.isNativePlatform()) return;

    let cancelled = false;
    requestNotificationPermission()
      .then(async (token) => {
        if (cancelled) return;
        const { receive } = await FirebaseMessaging.checkPermissions();
        setIsPermissionGranted(receive === 'granted');
        if (receive === 'granted' && !token) {
          console.warn('Native notification permission was granted without an FCM token.');
        }
      })
      .catch((error) => {
        console.error('Failed to initialize native push notifications:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      FirebaseMessaging.checkPermissions()
        .then(({ receive }) => setIsPermissionGranted(receive === 'granted'))
        .catch((error) => console.error('Failed to check native notification permission:', error));
      return;
    }

    // Check permission on mount
    if (typeof Notification !== 'undefined') {
      const currentPermission = Notification.permission;
      setIsPermissionGranted(currentPermission === 'granted');
      
      if (Capacitor.isNativePlatform()) {
        console.log(`[Android] Initial notification permission: ${currentPermission}`);
      }
    }

    // Listen for permission changes (when user grants from browser/device settings)
    const checkPermission = () => {
      if (typeof Notification !== 'undefined') {
        const currentPermission = Notification.permission;
        const wasGranted = isPermissionGranted;
        const isNowGranted = currentPermission === 'granted';
        
        if (wasGranted !== isNowGranted) {
          setIsPermissionGranted(isNowGranted);
          if (Capacitor.isNativePlatform() && isNowGranted) {
            console.log('[Android] Notification permission granted!');
          }
        }
      }
    };

    // Check periodically in case user grants permission from device settings
    // On Android, this is important as users might grant permission from system settings
    const interval = setInterval(checkPermission, 1000);
    
    return () => clearInterval(interval);
  }, [isPermissionGranted]);

  // Listen for messages when app is in foreground
  useEffect(() => {
    if (!isPermissionGranted) return;

    const messageListener = onMessageListener();
    
    messageListener.then((payload: any) => {
      // Show toast notification for foreground messages
      if (payload?.notification?.title) {
        toast.info(`${payload.notification.title}: ${payload.notification.body}`, {
          position: 'top-right',
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
        });
      }
    });
  }, [isPermissionGranted]);

  const requestPermission = async (): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      if (Capacitor.isNativePlatform()) {
        console.log('[Android] Requesting notification permission...');
      }
      
      const token = await requestNotificationPermission();
      const isGranted = token !== null;
      
      if (Capacitor.isNativePlatform()) {
        const { receive } = await FirebaseMessaging.checkPermissions();
        setIsPermissionGranted(receive === 'granted');
      } else if (typeof Notification !== 'undefined') {
        const actualPermission = Notification.permission;
        setIsPermissionGranted(actualPermission === 'granted');
      } else {
        setIsPermissionGranted(isGranted);
      }
      
      return isGranted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      if (Capacitor.isNativePlatform()) {
        console.error('[Android] Failed to request notification permission:', error);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isPermissionGranted,
    requestPermission,
    isLoading
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  
  return context;
}; 
